import "temporal-polyfill/global";
import "@/functions/promiseTryPolyfill";
import "@/toSorted.ts";
import { createApp, onMounted, provide, h, computed } from "vue";
import PrimeVue, { usePrimeVue } from "primevue/config";
// eslint-disable-next-line no-restricted-imports
import { useConfirm } from "primevue/useconfirm";
import "@fortawesome/fontawesome-free/css/all.css";
import "flag-icons/css/flag-icons.min.css";

import VueGridLayout from "vue3-drr-grid-layout";
import "vue3-drr-grid-layout/dist/style.css";
import ConfirmationService from "primevue/confirmationservice";
import ToastService, { type ToastServiceMethods } from "primevue/toastservice";
import { Settings } from "luxon";
import "@fi-solutions/lisa-ui/style.css";

import "@/assets/css/global.scss";
import "@/assets/css/globalVariables.scss";
import PortalToBreadcrumbs from "@/components/shared/PortalToBreadcrumbs.vue";

import router from "@/router";
import App from "@/App.vue";
import SetupUser from "@/modules/keycloak/views/SetupUser.vue";
import { captureBootstrapException, initializeSentry } from "./sentry";
import api from "@/api/api";

import { TimeZoneInjectionKey } from "./functions/timezoneUtil";
import store from "./store";
import { createStaticTranslationRuntime } from "./language/staticTranslations";

import { renderHealthPage } from "./health";
import { configureDynamicTranslationAccessToken } from "./language/dynamicTranslations";
import { initializeAuthorization } from "./functions/useAuthorization";

import "primeicons/primeicons.css"; // icons
import "primeflex/primeflex.css"; // utility classes
import { startRefreshKeycloakAccessTokenInterval } from "./keycloak/KeycloakRefresher";
import { KeycloakInjectionKey } from "./keycloak/KeycloakConfig";
import { initKeycloak } from "./keycloak/Keycloak";
import {
  completeAuthenticationCallback,
  ensureFreshAccessToken,
  initializeAuthenticationSession,
  isAuthenticationCallbackPage,
} from "./keycloak/AuthenticationSession";
import { OneDriveConfigInjectionKey, oneDriveConfig } from "./modules/oneDrive/utils/useOneDrive";
import type { ToastMessageOptions } from "primevue/toast";
import { lisaPrimeVueConfig } from "./primeVueConfig";

Settings.throwOnInvalid = true;

const isHealthPage = window.location.pathname.replace(/\/+$/, "") === "/health";

if (isAuthenticationCallbackPage()) {
  void completeAuthenticationCallback();
} else if (isHealthPage) {
  renderHealthPage();
} else {
  initKeycloak()
    .then(async ({ keycloak, auth }) => {
      if (auth === false) {
        await keycloak.login({ redirectUri: window.location.href });
      } else {
        initializeAuthenticationSession();
        configureDynamicTranslationAccessToken(ensureFreshAccessToken);
        const user = await store.dispatch("login/recoverSession");
        if (user) {
          await initializeAuthorization();
        }
        let isSetupNeeded = {
          state: false,
          token: "",
        };
        if (user === false) {
          // check if setup is needed
          isSetupNeeded = store.getters["login/needSetup"];
        }
        await createStaticTranslationRuntime(user?.language);
        const app = createApp({
          setup() {
            window.$primevue = usePrimeVue();

            /** provide timezone */
            provide(
              TimeZoneInjectionKey,
              computed(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
            );
            /** provide onedrive */
            provide(OneDriveConfigInjectionKey, oneDriveConfig);

            /** provide keycloak */
            provide(KeycloakInjectionKey, keycloak);
            onMounted(async () => {
              if (isSetupNeeded.state === true) {
                return;
              }
              store.dispatch("language/setLocale", user.language);
              if (
                user.changelogNotification &&
                typeof import.meta.env.__VERSION__ === "string" &&
                user.lastLoginVersion.trim() !== import.meta.env.__VERSION__.trim() &&
                import.meta.env.PROD
              ) {
                router.push("/changelog/new");
              }
            });
            startRefreshKeycloakAccessTokenInterval(keycloak);
          },
          render: () => {
            if (isSetupNeeded.state === true) {
              return h(SetupUser);
            }
            return h(App);
          },
        });
        initializeSentry(app, user);
        app.use(store);
        app.use(router);
        app.use(ConfirmationService);

        app.use(PrimeVue, lisaPrimeVueConfig);
        app.use(VueGridLayout);
        app.component("PortalBreadcrumbs", PortalToBreadcrumbs);
        app.use(ToastService);

        // initializeBootstrap();

        if (import.meta.env.DEV) {
          app.config.performance = true;
        } else {
          app.config.performance = false;
          // app.config.globalProperties = false;
        }

        app.config.globalProperties.$api = api;

        app.mount("#app");
      }
    })
    .catch((err: any) => {
      captureBootstrapException(err);
      // eslint-disable-next-line no-console
      console.error(err);
    });
}

interface EyeDropper {
  open: () => Promise<ColorSelectionResult>;
}

interface EyeDropperConstructor {
  new (): EyeDropper;
}

interface ColorSelectionResult {
  sRGBHex: string;
}

declare global {
  interface Window {
    $toast?: (
      message: Required<Pick<ToastMessageOptions, "summary" | "detail">> &
        Omit<ToastMessageOptions, "summary" | "detail">,
    ) => ReturnType<ToastServiceMethods["add"]>;
    $confirm?: ReturnType<typeof useConfirm>;
    $primevue?: ReturnType<typeof usePrimeVue>;
    EyeDropper?: EyeDropperConstructor;
  }
  interface Document {
    scannerDetectionData: undefined | any;
  }
}
